import { collection, getDocs } from "firebase/firestore";
import React, { useEffect, useState, useRef, Suspense } from "react";
import Masonry from "react-masonry-css";
import { db } from "../utils/firebase";
import { formatFollowers, getOptimizedImage, shareTextOf } from "../utils/Universalfunctions";
import LikeButton from "./LikeButton";
import { Link } from "react-router-dom";
import { Bookmark, Favorite, KeyboardArrowDown, RemoveRedEyeOutlined, Share } from "@mui/icons-material";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useUser } from "../context/UserContext";
import GalleryCategory from "../pages/GalleryCategory";
import Bookmarks from "./Promptcards/Bookmarks";
import SEO from "../config/SEO";



export default function MasonryGrid({ showhome }) {

      const breakpoints = {
            default: 4,
            1400: 4,
            1000: 3,
            700: 1,
            500: 1,
      };



      const [prompts, setPrompts] = useState([]);

      useEffect(() => {
            const fetchPrompts = async () => {
                  const querySnapshot = await getDocs(collection(db, "prompts"));
                  const list = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                  // console.log(list)
                  setPrompts(list);
            };

            fetchPrompts();
      }, []);


      const initialCount = Number(sessionStorage.getItem('homeVisibleCount')) || 30;
      const [visibleCount, setVisibleCount] = useState(
            showhome ? 35 : initialCount
      );
      const [category, setCategory] = useState('all');
      const [visibility, setVisibility] = useState("all");


      const parseCustomDate = (createdAt) => {
            if (!createdAt) return 0;

            // ✅ Case 1: Firestore Timestamp
            if (typeof createdAt === "object" && createdAt.seconds) {
                  return createdAt.seconds * 1000;
            }

            // ✅ Case 2: String date
            if (typeof createdAt === "string") {
                  try {
                        // With time → "13/12/2025, 10:30:45"
                        if (createdAt.includes(",")) {
                              const [datePart, timePart] = createdAt.split(", ");
                              const [day, month, year] = datePart.split("/").map(Number);
                              const [hour = 0, minute = 0, second = 0] = timePart.split(":").map(Number);

                              return new Date(year, month - 1, day, hour, minute, second).getTime();
                        }

                        // Without time → "13/12/2025"
                        const [day, month, year] = createdAt.split("/").map(Number);
                        return new Date(year, month - 1, day).getTime();
                  } catch (e) {
                        return 0;
                  }
            }

            return 0;
      };

      // Apply sort before filtering
      const sortedData = [...prompts].sort(
            (a, b) => parseCustomDate(b.createdAt) - parseCustomDate(a.createdAt)
      );

      // Filter apply sorted data par
      const filterData = sortedData.filter((item) => {
            if (item.status !== "success") return false;

            const itemVisibility = item.visibility || "public";

            // ✅ Free
            if (visibility === "free" && itemVisibility !== "public") {
                  return false;
            }

            // ✅ Private / Exclusive
            if (visibility === "private" && itemVisibility !== "private") {
                  return false;
            }

            // ✅ AutoBoost (NEW CONDITION)
            if (visibility === "autoboost" && item.autoboost !== true) {
                  return false;
            }

            // Category filter
            if (category === "all") return true;

            let tags = item.category;
            if (!tags || typeof tags !== "string") return false;

            const tagList = tags
                  .toLowerCase()
                  .split(",")
                  .map((t) => t.trim());

            return tagList.includes(category.toLowerCase());
      });



      const visibleData = filterData.slice(0, visibleCount);


      const getImageSrc = (prompt) => {
            // New prompts (Cloudinary)
            if (prompt) {
                  return getOptimizedImage({
                        publicId: prompt,
                        width: 1200,
                        watermark: true,
                  });
            }

            // Old prompts (imgbb fallback)
            return prompt; // imgbb URL
      };

      const loaderRef = useRef(null);


      useEffect(() => {
            if (showhome) return;

            const currentLoader = loaderRef.current; // snapshot of ref

            const observer = new IntersectionObserver(
                  (entries) => {
                        if (entries[0].isIntersecting) {
                              setVisibleCount((prev) => {
                                    // ✅ Already full loaded
                                    if (prev >= filterData.length) return prev;

                                    const newCount = Math.min(prev + 20, filterData.length);

                                    sessionStorage.setItem("homeVisibleCount", newCount);

                                    return newCount;
                              });
                        }
                  },
                  { threshold: 1 }
            );

            if (currentLoader) {
                  observer.observe(currentLoader);
            }

            return () => {
                  if (currentLoader) {
                        observer.unobserve(currentLoader); // cleanup safely
                  }
            };
      }, [filterData.length, showhome]);



      const { isPremium, userData } = useUser();


      const getCategoryContent = (category) => {
            switch (category) {
                  case "all":
                        return {
                              title: "Explore All Prompts 🚀",
                              subtitle: "See what the community is creating and loving right now."
                        };

                  case "free":
                        return {
                              title: "Free Prompts — No Limits, Just Creativity ✨",
                              subtitle: "See what creators are building without restrictions."
                        };

                  case "private":
                        return {
                              title: "Exclusive Team Access 🔐",
                              subtitle: "Unlock what our top community members are creating."
                        };

                  case "autoboost":
                        return {
                              title: "AI Auto-Boosted Prompts ⚡",
                              subtitle: "Discover prompts the system elevated for maximum impact."
                        };

                  default:
                        return {
                              title: "Discover Powerful Prompts",
                              subtitle: "See what the community is creating right now."
                        };
            }
      };


      const categoryContent = getCategoryContent(visibility);


      const handleScroll = () => {
            window.scrollBy({
                  top: window.innerHeight * 0.8,
                  behavior: "smooth",
            });
      };


      return (
            <>

            {showhome !== true && 
                  <SEO
                        title="Prompt Gallery"
                        description="Browse a curated gallery of high quality AI prompts."
                  />
            }

                  <div className={`preview-items ${showhome && 'pt-0'}`}>
                        <div className={`__container ${showhome && 'ps-0 pe-0'}`}>

                              {showhome !== true &&
                                    <div className="visibility-tabs">

                                          <div className="visibility-head">
                                                <h1>{categoryContent.title}</h1>
                                                <p>{categoryContent.subtitle}</p>
                                          </div>
                                          <div className='visibility-in-tabs'>

                                                <button
                                                      className={visibility === "all" ? "active" : ""}
                                                      onClick={() => setVisibility("all")}
                                                >
                                                      All
                                                </button>

                                                <button
                                                      className={visibility === "free" ? "active" : ""}
                                                      onClick={() => setVisibility("free")}
                                                >
                                                      Free
                                                </button>

                                                <button
                                                      className={visibility === "private" ? "active active-indicator" : ""}
                                                      onClick={() => setVisibility("private")}>
                                                      Exclusive
                                                </button>

                                                <button
                                                      className={visibility === "autoboost" ? "active active-indicator" : ""}
                                                      onClick={() => setVisibility("autoboost")}>
                                                      Boosted
                                                </button>
                                          </div>
                                    </div>
                              }

                              <GalleryCategory
                              showhome={showhome}
                                    data={filterData}
                                    setCategory={setCategory}
                                    category={category}
                                    visibleData={visibleData}
                              />

                              <Suspense fallback={<p className='text-center'>Loading</p>}>

                                    <Masonry
                                          breakpointCols={breakpoints}
                                          className="masonry-grid"
                                          columnClassName="masonry-column"
                                    >
                                          {visibleData.map((card, i) => (
                                                <div className={`items ${card?.visibility === "private" ? "private-item" : ""}`}
                                                      style={{
                                                            "--delay": `${Math.random() * 4}s`,
                                                            "--duration": `${1.2 + Math.random() * 1.5}s`
                                                      }}
                                                      key={i}>
                                                      <img
                                                            loading="lazy"
                                                            src={getImageSrc(card?.imageUrl)} alt={card?.title} />

                                                      {card?.visibility === "private" &&
                                                            <>
                                                                  <OverlayTrigger
                                                                        placement="bottom"
                                                                        overlay={
                                                                              <Tooltip id="premium-tooltip">
                                                                                    This prompt is for <b>Exclusive users</b>.
                                                                              </Tooltip>
                                                                        }
                                                                  >
                                                                        <Link to="/pricing" className="premium-button">
                                                                              <span className="premium-icon">🜲</span>
                                                                              <span className="premium-text">Exclusive</span>
                                                                        </Link>
                                                                  </OverlayTrigger>
                                                            </>
                                                      }


                                                      {isPremium !== "paid" && card?.visibility === "private" ?
                                                            <div className="subscribe-card">
                                                                  <div className="subscribe-badge"> 🜲 </div>
                                                                  <h2>Subscribe to see exclusive content <Link to="/pricing">here</Link></h2>
                                                            </div>

                                                            :

                                                            <div className="items-bottom-bar">
                                                                  <div className="preimium-label"> {card?.aiModel.toUpperCase()}  </div>
                                                                  <p className="prompt-text">{card?.promptText?.slice(0, 60) || "No prompt text available"}...</p>
                                                                  <div className="items-servieces">
                                                                        <div className="items-view-icon"> <Favorite /> <span>{formatFollowers(card?.likes) || 0}</span> </div>
                                                                        <div className="items-view-icon"> <RemoveRedEyeOutlined /> <span>{card?.view ? formatFollowers(card?.view + card?.likes ) : '0' || 0}</span> </div>
                                                                        <div className="items-view-icon"> <Bookmark /> <span>{card?.bookmarks ? formatFollowers(card?.bookmarks) : '0' || 0}</span> </div>
                                                                  </div>
                                                            </div>
                                                      }




                                                      <Link to={`/prompts/${card?.id}`} state={card} className="stretched-link" >  </Link>
                                                      <LikeButton card={card} />

                                                      <button onClick={() => shareTextOf(`${window.location.origin}/prompts/${card?.id}`)}
                                                            type="button" className="card-feature-btn share-button">
                                                            <Share />
                                                      </button>

                                                      <Bookmarks
                                                            userData={userData}
                                                            visibleData={card} />
                                                </div>
                                          ))}
                                    </Masonry>
                              </Suspense>

                              {!showhome && visibleCount < filterData.length &&
                                    <div ref={loaderRef} className="infinite-loader">
                                          Loading more prompts...
                                    </div>
                              }
                        </div>
                  </div>

                  {!showhome && visibleCount < filterData.length &&

                        <button className="scroll-down-btn" onClick={handleScroll}>
                              <span className="icon-wrapper">
                                    <KeyboardArrowDown className="arrow-icon" />
                              </span>
                              <span className="scroll-text">Scroll</span>
                        </button>
                  }
            </>

      );
}